import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Phone, Globe, CheckCircle, Upload, X, Loader2, ArrowLeft, ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfileSettings() {
  const { user, authFetch, setUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    name: '', email: '', phone: '', title: '', bio: '', profile_photo: '', logo_url: '', website: '',
  });
  const [password, setPassword] = useState({ old: '', new: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });
  const [uploading, setUploading] = useState('');
  const [toast, setToast] = useState('');
  const photoRef = useRef(null);
  const logoRef = useRef(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3200); };

  const isBrandingRole = ['agent', 'seller'].includes(user?.role);

  useEffect(() => {
    authFetch(`${API}/api/users/me`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setProfile({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            title: data.title || '',
            bio: data.bio || '',
            profile_photo: data.profile_photo || '',
            logo_url: data.logo_url || '',
            website: data.website || '',
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const setField = (key, val) => setProfile(prev => ({ ...prev, [key]: val }));

  const handleFileUpload = async (file, field) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(field);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await authFetch(`${API}/api/upload`, { method: 'POST', body: fd });
      if (r.ok) {
        const { url } = await r.json();
        setField(field, url);
      }
    } catch (e) { console.error('Upload error', e); }
    setUploading('');
  };

  const handleSaveProfile = async () => {
    if (!profile.name.trim()) { setProfileMsg({ type: 'error', text: 'Name is required' }); return; }
    setSavingProfile(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const body = {};
      if (profile.name) body.name = profile.name;
      if (profile.email) body.email = profile.email;
      if (isBrandingRole) {
        if (profile.phone) body.phone = profile.phone;
        if (profile.title) body.title = profile.title;
        if (profile.bio) body.bio = profile.bio;
        if (profile.profile_photo) body.profile_photo = profile.profile_photo;
        if (profile.logo_url) body.logo_url = profile.logo_url;
        if (profile.website) body.website = profile.website;
      }
      const r = await authFetch(`${API}/api/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const data = await r.json();
        setUser(data);
        setProfileMsg({ type: 'success', text: 'Profile saved successfully!' });
        showToast('Profile saved!');
      } else {
        const err = await r.json();
        setProfileMsg({ type: 'error', text: err.detail || 'Failed to save profile' });
      }
    } catch { setProfileMsg({ type: 'error', text: 'Something went wrong' }); }
    setSavingProfile(false);
  };

  const handleChangePassword = async () => {
    if (!password.old || !password.new) { setPasswordMsg({ type: 'error', text: 'All fields required' }); return; }
    if (password.new !== password.confirm) { setPasswordMsg({ type: 'error', text: 'New passwords do not match' }); return; }
    if (password.new.length < 6) { setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters' }); return; }
    setSavingPassword(true);
    setPasswordMsg({ type: '', text: '' });
    try {
      const r = await authFetch(`${API}/api/users/me/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_password: password.old, new_password: password.new }),
      });
      if (r.ok) {
        setPassword({ old: '', new: '', confirm: '' });
        setPasswordMsg({ type: 'success', text: 'Password changed!' });
        showToast('Password changed!');
      } else {
        const err = await r.json();
        setPasswordMsg({ type: 'error', text: err.detail || 'Failed to change password' });
      }
    } catch { setPasswordMsg({ type: 'error', text: 'Something went wrong' }); }
    setSavingPassword(false);
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-100 transition-all";
  const labelCls = "text-xs font-semibold text-slate-500 mb-1.5 block";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-3 rounded-xl shadow-xl">
            <CheckCircle size={15} className="text-emerald-400" />{toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center gap-3 mb-7">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">
          <ArrowLeft size={16} />Back
        </button>
        <div className="h-5 w-px bg-slate-200" />
        <div>
          <h1 className="text-2xl font-black" style={{ color: '#0A192F' }}>Profile Settings</h1>
        </div>
      </div>

      {/* Account Info */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <User size={15} className="text-slate-500" />
          </div>
          <h2 className="font-bold text-slate-800">Account</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Display Name</label>
            <input type="text" value={profile.name} onChange={e => setField('name', e.target.value)}
              data-testid="settings-name" className={inputCls} placeholder="Your full name" />
          </div>
          <div>
            <label className={labelCls}>Email Address</label>
            <input type="email" value={profile.email} onChange={e => setField('email', e.target.value)}
              data-testid="settings-email" className={inputCls} placeholder="your@email.com" />
          </div>

          {profileMsg.text && (
            <div className={`text-xs font-medium px-3 py-2 rounded-lg ${profileMsg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}
              data-testid="settings-profile-msg">
              {profileMsg.text}
            </div>
          )}

          <button onClick={handleSaveProfile} disabled={savingProfile}
            data-testid="save-account-btn"
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#0A192F' }}>
            {savingProfile ? 'Saving...' : 'Save Account'}
          </button>
        </div>
      </motion.div>

      {/* Change Password */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Lock size={15} className="text-slate-500" />
          </div>
          <h2 className="font-bold text-slate-800">Change Password</h2>
        </div>

        <div className="space-y-3">
          <input type="password" placeholder="Current password" value={password.old}
            onChange={e => setPassword(p => ({ ...p, old: e.target.value }))}
            data-testid="settings-old-password" className={inputCls} />
          <input type="password" placeholder="New password (min 6 chars)" value={password.new}
            onChange={e => setPassword(p => ({ ...p, new: e.target.value }))}
            data-testid="settings-new-password" className={inputCls} />
          <input type="password" placeholder="Confirm new password" value={password.confirm}
            onChange={e => setPassword(p => ({ ...p, confirm: e.target.value }))}
            data-testid="settings-confirm-password" className={inputCls} />

          {passwordMsg.text && (
            <div className={`text-xs font-medium px-3 py-2 rounded-lg ${passwordMsg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}
              data-testid="settings-password-msg">
              {passwordMsg.text}
            </div>
          )}

          <button onClick={handleChangePassword} disabled={savingPassword || !password.old || !password.new}
            data-testid="save-password-btn"
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#0A192F' }}>
            {savingPassword ? 'Updating...' : 'Change Password'}
          </button>
        </div>
      </motion.div>

      {/* Agent Branding — agent/seller only */}
      {isBrandingRole && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
              <ImageIcon size={15} className="text-slate-500" />
            </div>
            <h2 className="font-bold text-slate-800">Agent Branding</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5 ml-10">Shown to buyers on your listing detail pages</p>

          <div className="space-y-5">
            {/* Profile Photo */}
            <div>
              <label className={labelCls}>Profile Photo</label>
              <div className="flex items-center gap-4">
                {profile.profile_photo ? (
                  <div className="relative flex-shrink-0">
                    <img src={profile.profile_photo} alt="Profile"
                      className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
                    <button onClick={() => setField('profile_photo', '')}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X size={10} />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <User size={24} className="text-slate-300" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <button onClick={() => photoRef.current?.click()}
                    data-testid="upload-profile-photo-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                    {uploading === 'profile_photo' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Upload Photo
                  </button>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden"
                    onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0], 'profile_photo')} />
                  <input type="url" placeholder="Or paste photo URL..." value={profile.profile_photo}
                    onChange={e => setField('profile_photo', e.target.value)}
                    className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400 transition-all" />
                </div>
              </div>
            </div>

            {/* Agency Logo */}
            <div>
              <label className={labelCls}>Agency Logo</label>
              {profile.logo_url && (
                <div className="mb-2 flex items-center gap-2">
                  <img src={profile.logo_url} alt="Logo"
                    className="h-10 max-w-32 object-contain border border-slate-200 rounded-lg px-2 py-1" />
                  <button onClick={() => setField('logo_url', '')}
                    className="w-5 h-5 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0">
                    <X size={10} />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => logoRef.current?.click()}
                  data-testid="upload-logo-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all flex-shrink-0">
                  {uploading === 'logo_url' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Upload Logo
                </button>
                <input ref={logoRef} type="file" accept="image/*" className="hidden"
                  onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0], 'logo_url')} />
                <input type="url" placeholder="Or paste logo URL..." value={profile.logo_url}
                  onChange={e => setField('logo_url', e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className={labelCls}>
                <Phone size={11} className="inline mr-1" />Phone Number
              </label>
              <input type="tel" placeholder="(480) 555-0100" value={profile.phone}
                onChange={e => setField('phone', e.target.value)}
                data-testid="settings-phone" className={inputCls} />
            </div>

            {/* Title */}
            <div>
              <label className={labelCls}>Title / Role</label>
              <input type="text" placeholder="Licensed Real Estate Agent" value={profile.title}
                onChange={e => setField('title', e.target.value)}
                data-testid="settings-title" className={inputCls} />
            </div>

            {/* Bio */}
            <div>
              <label className={labelCls}>Short Bio</label>
              <textarea rows={3} placeholder="Tell buyers about yourself and your expertise..."
                value={profile.bio} onChange={e => setField('bio', e.target.value)}
                data-testid="settings-bio"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all resize-none" />
            </div>

            {/* Website */}
            <div>
              <label className={labelCls}>
                <Globe size={11} className="inline mr-1" />Website
              </label>
              <input type="url" placeholder="https://yoursite.com" value={profile.website}
                onChange={e => setField('website', e.target.value)}
                data-testid="settings-website" className={inputCls} />
            </div>

            {profileMsg.text && (
              <div className={`text-xs font-medium px-3 py-2 rounded-lg ${profileMsg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
                {profileMsg.text}
              </div>
            )}

            <button onClick={handleSaveProfile} disabled={savingProfile}
              data-testid="save-branding-btn"
              className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: '#0A192F' }}>
              {savingProfile ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
